import { useMemo } from "react";
import useZustandStore from "../zustandStore/useZustandStore";
import {
  SEEKHO_EMAIL_ROUTES,
  getRequiredPermissionsForRoute,
  getRequiredPermissionsForNavLink,
  handleIsSeekhoEmail,
} from "../utils/constants/permissions";

export const usePermissions = () => {
  const userPermissions = useZustandStore((s) => s.userPermissions ?? []);
  const roles = useZustandStore((s) => s?.currentUser?.roles);
  const email = useZustandStore((s) => s?.currentUser?.email);

  // Mirror the backend's has_permission(): super_admin passes every check.
  // Without this the UI hides features whose permission row hasn't been seeded
  // yet (a new permission is invisible until its migration runs), even though
  // the API would authorize the call.
  const isSuperAdmin = Array.isArray(roles)
    ? roles.includes("super_admin")
    : roles === "super_admin";

  const isSeekhoEmail = handleIsSeekhoEmail({ email });

  return useMemo(() => {
    const handleEmailAllows = ({ path }) =>
      isSeekhoEmail || !SEEKHO_EMAIL_ROUTES.includes(path);
    return {
      permissions: userPermissions,
      isSuperAdmin,
      hasPermission: (code) =>
        isSuperAdmin ? true : code ? userPermissions.includes(code) : false,
      hasAnyPermission: (codes) =>
        isSuperAdmin ||
        (Array.isArray(codes) && codes.some((c) => userPermissions.includes(c))),
      hasRouteAccess: (path) => {
        if (!handleEmailAllows({ path })) return false;
        if (isSuperAdmin) return true;
        const required = getRequiredPermissionsForRoute(path);
        if (required.length === 0) return true;
        return required.some((c) => userPermissions.includes(c));
      },
      /** Granular sidebar / deep links — uses NAV_LINK_PERMISSIONS when defined. */
      hasNavLinkAccess: (to) => {
        if (!handleEmailAllows({ path: (to || "").split(/[?#]/)[0] })) {
          return false;
        }
        if (isSuperAdmin) return true;
        const required = getRequiredPermissionsForNavLink(to);
        if (required.length === 0) return true;
        return required.some((c) => userPermissions.includes(c));
      },
    };
  }, [userPermissions, isSuperAdmin, isSeekhoEmail]);
};

export default usePermissions;
